(ns ncr.fragment.props.timesheet
  (:require
   ["bignumber.js" :as big]
   [clojure.string :as str]
   [clojure.set :as set]
   [clojure.math :as math]
   [goog.style :as gs]
   [react :as react]
   [lambdaisland.glogi :as log]
   [ncr.backend :as b]
   [cljs.core.async :as async :refer-macros [go] :refer [<!]]
   [ncr.etc.alert :as a]
   [ncr.etc.modal :as m]
   [ncr.etc.pubsub :as ps]
   [ncr.etc.profile-pspec :as pp]
   [ncr.etc.util :as u]
   [ncr.widgets.pf :as wpf]
   [uix.compiler.alpha :as uix.compiler]
   [uix.core.alpha :as uix]
   [ncr.fragment.props.date :as props-date]
   [ncr.fragment.common :as fragment-common]
   [ncr.fragment.props.interval :as props-interval]
   [ncr.widgets.core :as w]
   [ncr.etc.pf :as pf]
   [ncr.me :as me]
   [goog.events.KeyCodes :as KeyCodes]))

(def PROPERTY_KEY :timesheet/data)
(def WORKING_HOURS_PER_DAY 8)

(def ^:private viewport-parent-id "intervals-list")
(def ^:private task-item-class "ncr-c-property-timesheet__task")
(defn- task-item-uid [uid] (str "." task-item-class "[data-uid='" uid "']"))
(def ^:private interval-item-class "ncr-c-property-timesheet__interval")
(defn- interval-item-uid [uid] (str "." interval-item-class "[data-uid='" uid "']"))

(defn- hours->duration-str [hours display-settings]
  (let [{:keys [lowercase? days-no-space?]} display-settings
        d (math/floor (/ hours WORKING_HOURS_PER_DAY))
        h (- hours (* d WORKING_HOURS_PER_DAY))
        days (when (pos? d) (str d (if lowercase? "d" "D")))
        hours (when (or (pos? h) (not days)) (str h (if lowercase? "h" "H")))]
    (str/join (if days-no-space? "" " ") (filter some? [days hours]))))

(defn- hours->primary-secondary [hours display-settings]
  (let [hours-str (str hours (if (:lowercase? display-settings) "h" "H"))
        day-str (hours->duration-str hours display-settings)]
    (if (:days-first? display-settings)
      [day-str hours-str]
      [hours-str day-str])))

(defn- duration-str->hours [s]
  (->> s str/trim str/lower-case
    (re-seq #"\d+(?:\.\d+)?[dh]?")
    (reduce #(+ %1 (* (js/parseFloat (re-find #"\d+(?:\.\d+)?" %2)) (if (re-find #"d" %2) WORKING_HOURS_PER_DAY 1))) 0)
    js/Math.round))

(defn duration-input [{:keys [value on-change *ref display-settings]}]
  [pf/c-input-group
   (let [get-input-value #(first (hours->primary-secondary % display-settings))
         *val (uix/state (get-input-value value))
         val @*val
         hours (duration-str->hours val)
         on-blur (fn [with-enter?]
                   (reset! *val (get-input-value hours))
                   (when (not= hours value)
                     (on-change hours with-enter?)))]

     (uix/effect! #(reset! *val (get-input-value value)) [value])
     (uix/effect! #(swap! *val (comp get-input-value duration-str->hours)) [display-settings])

     [:<>
      [pf/c-text-input
       {:value val
        :key value
        :ref *ref
        :on-focus (fn []
                    (when-let [input @*ref]
                      (.select input))
                    (when (zero? hours)
                      (reset! *val "")))
        :on-blur on-blur

        :on-key-down
        (u/isolate
          (fn [^js event]
            (condp = (.-keyCode event)
              KeyCodes/ENTER (do (on-blur true) (.blur ^js @*ref) true)
              KeyCodes/ESC (do
                             (if (not= val (str value))
                               (reset! *val (str value))
                               (.blur ^js @*ref))
                             true)
              nil)))
        :on-change #(reset! *val %)}]
      [pf/c-input-group-text {:variant :plain}
       (last (hours->primary-secondary hours display-settings))]])])

(defn- scroll-offset [^js element ^js viewport]
  (let [top-margin 90
        bottom-margin 0
        ^js vb (gs/getBounds viewport)
        ^js eb (gs/getBounds element)
        top (- (.-top eb) (.-top vb))
        bottom (- (.-y (.getBottomRight eb)) (.-y (.getBottomRight vb)))]
    (cond
      (< top top-margin) (- top top-margin)
      (> bottom bottom-margin) (- bottom bottom-margin)
      :else nil)))

(defn- use-pending-scroll []
  (let [*scroll-items (uix/state nil)
        scroll-items @*scroll-items]
    (uix/effect!
      (fn []
        (when scroll-items
          (let [viewport (js/document.querySelector (str "#" viewport-parent-id " > .pf-c-form__field-group-body"))
                {:keys [mn mx]}
                (->> scroll-items
                  (map #(scroll-offset (js/document.querySelector %) viewport))
                  doall
                  (filter some?)
                  (reduce
                    (fn [{:keys [mn mx] :as acc} offset]
                      (cond
                        (and (pos? offset) (or (not mx) (< mx offset))) (assoc acc :mx offset)
                        (and (neg? offset) (or (not mn) (> mn offset))) (assoc acc :mn offset)
                        :else acc)) {}))]
            (when-let [d (or mn mx)]
              (.scrollTo viewport #js {:top (+ (.-scrollTop viewport) d) :behavior "smooth"})))
          (reset! *scroll-items nil)))
      [scroll-items])
    #(reset! *scroll-items %)))

(defn- label-input [{:keys [label id value on-change]}]
  (let [me (uix/context me/*context*)
        labels (get-in me [:cluster :labels])
        selected (when value
                   (some #(when (= value (:id %)) %) labels))]
    [pf/c-form-group {:label label :field-id id}
     [wpf/c-pf-select
      {:class :ncr-c-property-timesheet__person-select
       :label-fn :name
       :toggle-id id
       :variant :typeahead
       :focus-input? false
       :selections selected
       :max-height "200px"
       :menu-append-to #(.-body js/document)
       :on-select
       (fn [_ {:keys [id]} _]
         (on-change (if (= value id) nil id)))}
      (->> labels
        (sort-by :name)
        (map
          (fn [label]
            (assoc label :render-fn
              (fn [{:keys [value]}]
                [[:span.ncr-pf-c-select-option-label-truncated (:label value)]])))))]]))

(defn- task-group-header [{:keys [value title count display-settings]}]
  (let [[primary secondary] (hours->primary-secondary value display-settings)]
    [pf/c-split {:has-gutter true :class [:ncr-c-property-timesheet__task-group :pf-u-w-100 (keyword task-item-class)] :data-uid title}
     [pf/c-split-item {:class :ncr-c-property-timesheet__task-indicator}]
     [pf/c-split-item {:class :ncr-c-property-timesheet__task-group__title} title
      [:span.ncr-c-timesheet-title__tasks " "
       [w/c-mdi-icon {:icon :checkbox-marked-outline}]
       count]]
     [pf/c-split-item
      {:class [:ncr-c-property-timesheet__task-group__value :ncr-c-property-timesheet__task-group__value-primary]}
      primary]
     [pf/c-split-item
      {:class [:ncr-c-property-timesheet__task-group__value :ncr-c-property-timesheet__task-group__value-secondary]}
      secondary]
     [pf/c-split-item {:class :pf-m-fill}]]))

(defn- task [{:keys [uid id-prefix value on-change display-settings]}]
  (let [[p v] value
        *duration-ref (uix/ref nil)]
    [pf/c-split {:has-gutter true :class [:pf-u-w-100 (keyword task-item-class)] :data-uid uid}
     [pf/c-split-item {:class :ncr-c-property-timesheet__task-indicator}]
     [pf/c-split-item
      [label-input
       {:key uid
        :id (str id-prefix "_label")
        :value p
        :on-change
        (fn [label]
          (when-let [^js duration-input @*duration-ref] (.focus duration-input))
          (on-change [label v]))}]]
     [pf/c-split-item
      (let [{:keys [^js actual]} v
            value (if actual (.toNumber actual) 0)]
        [duration-input
         {:*ref *duration-ref
          :key value
          :value value
          :on-change (fn [v with-enter?]
                       (on-change [p {:actual (big/BigNumber. v)}] with-enter?))
          :display-settings display-settings}])]
     [pf/c-split-item {:class :ncr-c-property-timesheet__task-toolbar}
      [w/c-toolbar-button
       {:icon :delete-outline
        :on-click #(on-change nil)
        :tooltip "Delete task"}]]
     [pf/c-split-item {:class :pf-m-fill}]]))

(defn- tasks-hours [tasks]
  (->> tasks vals (map :actual) (remove nil?) (map (fn [^js bn] (.toNumber bn))) (apply +)))

(defn- tasks-hours-icons [{:keys [tasks display-settings]}]
  [:<>
   (let [tasks-cnt (apply + (map count tasks))]
     [pf/c-tooltip {:content [:span (str tasks-cnt " tasks")]}
      [:span.ncr-c-timesheet-title__tasks " "
       [w/c-mdi-icon {:icon :checkbox-marked-outline}]
       tasks-cnt]])
   (let [hours (apply + (map tasks-hours tasks))
         [primary secondary] (hours->primary-secondary hours display-settings)]
     [:span.ncr-c-timesheet-title__value " "
      [w/c-mdi-icon {:icon :clock-outline}]
      primary
      (when (>= hours WORKING_HOURS_PER_DAY)
        [:span {:style {:opacity 0.75 :font-size "85%"}}
         (str " (" secondary ")")])])])

(defn- get-update-tasks [value old-id idx]
  (fn [[id values]]
    (let [old-task (get value id)]
      (if (or (= old-id id) (not old-task))
        (let [values (or values (when (and (string? id) (not (string? old-id))) {:actual (big/BigNumber. 0)}))]
          [(u/assoc-or-dissoc (dissoc value old-id) id values) (if values #(assoc % idx id) #(vec (u/remove-nth idx %)))])
        [value identity]))))

(defn- summarize-labels [label-ids get-value get-name]
  (letfn [(summarize-tree [tree]
            (reduce-kv
              (fn [acc k v]
                (if (map? v)
                  (let [summarized (summarize-tree v)
                        child-count (count (filter map? (vals summarized)))]
                    (cond
                      (zero? child-count) (assoc acc k v)
                      (= 1 child-count) (merge acc summarized)
                      :else (assoc acc k
                              {:id (:id (first (sort-by :name (vals summarized))))
                               :value (reduce + (map :value (vals summarized)))
                               :count child-count
                               :summarized true})))
                  (assoc acc k v)))
              {} tree))

          (flatten-tree [tree prefix]
            (reduce-kv
              (fn [acc k v]
                (if (:summarized v)
                  (assoc acc (:id v) {:title (str prefix k) :value (:value v) :count (:count v)})
                  (if (map? v)
                    (merge acc (flatten-tree v (str prefix k "/")))
                    acc)))
              {} tree))]

    (let [label-tree (reduce
                       (fn [acc id]
                         (assoc-in acc (str/split (get-name id) #"/") {:id id :value (get-value id)}))
                       {} label-ids)
          summarized (summarize-tree label-tree)]
      (flatten-tree summarized ""))))

(defn- task-list [{:keys [value on-change group-uid display-settings]}]
  (let [me (uix/context me/*context*)
        labels (uix/memo #(u/index-by :id (get-in me [:cluster :labels])) [me])
        *sorted (uix/state (fn [] (vec (sort-by #(get-in labels [% :name]) (keys value)))))
        set-pending-scroll (use-pending-scroll)
        *pending-focus (uix/state nil)
        sorted @*sorted
        last-idx (dec (count sorted))
        pending-focus @*pending-focus
        {:keys [show-task-groups?]} display-settings
        summarized-labels
        (uix/memo
          (fn []
            (when show-task-groups?
              (summarize-labels sorted
                #(let [^js n (get-in value [% :actual])] (if n (.toNumber n) 0))
                #(get-in labels [% :name])))) [labels value sorted show-task-groups?])]

    (uix/effect!
      (fn []
        (let [new-ids (set (keys value))
              old-ids (set sorted)
              diff (set/difference new-ids old-ids)]
          (when-let [last-id (last diff)]
            (reset! *pending-focus (str "#" last-id "_label-select-typeahead"))
            (set-pending-scroll [(task-item-uid last-id) (str "[data-uid='" group-uid "']")])
            (reset! *sorted (vec (concat sorted diff))))))
      [value sorted group-uid])

    (uix/effect!
      (fn []
        (when-let [^js item (and pending-focus (js/document.querySelector pending-focus))]
          (.focus item)
          (reset! *pending-focus nil)))
      [pending-focus])

    [pf/c-form-field-group
     {:class :ncr-c-property-timesheet__task-list}
     (for [[idx k] (map-indexed vector sorted)
           :let [v (get value k)
                 update-tasks (get-update-tasks value k idx)
                 group-data (get summarized-labels k)]]
       [:<>
        {:key (str idx (when (seq group-data) "_group"))}
        (when (seq group-data)
          [task-group-header (assoc group-data :display-settings display-settings)])
        [task
         ; usually this is a bad idea to use index-based keys, but we do not want to invalidate the whole task on label change
         ; the better solution would be to generate internal uids
         {:uid k
          :id-prefix (str k)
          :value [(when-not (keyword? k) k) v]
          :display-settings display-settings
          :on-change
          (fn [val with-enter?]
            (when with-enter?
              (let [selector (str "#" (get sorted (inc idx)) "_label-select-typeahead")]
                (when-let [^js next (js/document.querySelector selector)]
                  (.select next))))
            (let [[new-value update-sorted] (update-tasks val)]
              (swap! *sorted update-sorted)
              (on-change
                (merge new-value
                  (when (and (= idx last-idx) with-enter?) {(gensym) {}})))))}]])]))

(defn- interval [{:keys [uid value idx on-change on-add-interval display-settings]}]
  (let [id (uix/memo gensym [])
        tasks (get value :tasks {})
        control-group-uid (str "add-task-" uid)]

    [pf/c-form-field-group {:class (keyword interval-item-class) :data-uid uid}
     [:div {:class :ncr-c-property-timesheet__interval-header}
      [pf/c-split {:class :pf-u-w-100 :has-gutter true}
       [pf/c-split-item {:class :ncr-c-property-timesheet__interval-header__index}
        "#" [:span {:class :ncr-c-property-timesheet__interval-header__index-value} (inc idx)]]
       [pf/c-split-item
        [props-interval/input
         {:id id
          :add-from? true
          :add-count? true
          :value (select-keys value [:start-date :end-date])
          :on-change #(on-change (merge value %))}]]
       [pf/c-split-item {:class :pf-m-fill :style {:padding-bottom 24}}
        [tasks-hours-icons {:tasks [tasks] :display-settings display-settings}]]
       [pf/c-split-item {:class :ncr-c-property-timesheet__interval-toolbar}
        [w/c-toolbar-button
         {:icon :delete-outline :on-click #(on-change nil) :tooltip "Delete interval"}]]]]
     [task-list
      {:group-uid control-group-uid
       :value (get value :tasks {})
       :display-settings display-settings
       :on-change #(on-change (assoc value :tasks %))}]
     [:div {:class :ncr-c-property-timesheet__interval-footer
            :data-uid control-group-uid}
      [pf/c-form-field-group-header
       {:actions
        [:<>
         [pf/c-button
          {:variant :secondary
           :on-click #(on-change (assoc-in value [:tasks (gensym)] {}))}
          "Add task"]
         (when on-add-interval
           [pf/c-button
            {:variant :primary
             :style {:margin-left 15}
             :on-click on-add-interval}
            "Add interval"])]}]]]))

(defn- timesheet-title [{:keys [intervals person wrap? show-time? display-settings] :or {show-time? true}}]
  (let [cnt (count intervals)]
    (if (pos? cnt)
      (let [tasks (map :tasks intervals)
            [from to] (map props-date/to-string [(first (sort (map :start-date intervals))) (last (sort (map :end-date intervals)))])]
        [:span.ncr-c-timesheet-title (when wrap? {:class :wrapped})
         [:span.ncr-c-timesheet-title__interval
          (if (and show-time? (not (or (str/blank? from) (str/blank? to))))
            [:<>
             [:span.ncr-c-timesheet-title__interval-from from]
             [:span.ncr-c-timesheet-title__interval-dash " — "]
             [:span.ncr-c-timesheet-title__interval-to to]
             (when (> cnt 1)
               [:span.ncr-c-timesheet-title__interval-count " (" cnt ")"])]
            (when (> cnt 1)
              [:span.ncr-c-timesheet-title__interval-count cnt]))]
         [:span
          (when-let [label-name (some->> person (me/label-by-id (uix/context me/*context*)) :name)]
            [:span.ncr-c-timesheet-title__person
             (str "#" label-name)])
          [tasks-hours-icons {:tasks tasks :display-settings display-settings}]]])
      "No intervals")))

(defn- get-update-in-vector [v i]
  #(if % (assoc v i %) (vec (u/remove-nth i v))))

(def ^:private timesheet-title*
  (uix.compiler/as-react #(timesheet-title %)))

(defn- sanitize-interval [{:keys [tasks] :as interval}]
  (let [tasks (->> tasks seq (map (fn [[k v]] (when (string? k) [k v]))) (filter some?) (into {}))]
    (when (seq tasks)
      (-> interval (assoc :tasks tasks) (dissoc :_uid)))))

(defn- sanitize-data [{:keys [intervals] :as data}]
  (->> intervals (map sanitize-interval) (filter some?) vec (assoc data :intervals)))

(defn- interval-list [{:keys [value on-change display-settings]}]
  (let [intervals (uix/memo (fn [] (mapv #(if (:_uid %) % (assoc % :_uid (gensym))) value)) [value])
        get-update (uix/callback #(get-update-in-vector intervals %) [intervals])
        max-interval-idx (dec (count intervals))
        set-pending-scroll (use-pending-scroll)]

    [pf/c-form-field-group
     {:id viewport-parent-id
      :header
      [pf/c-form-field-group-header
       (merge
         {:title-text #js {:id   "intervals-list-title"
                          :text (react/createElement timesheet-title*
                                  #js {:intervals intervals :display-settings display-settings})}}
         (when (empty? intervals)
           {:actions
            [pf/c-button
             {:variant :secondary
              :on-click
              (fn []
                (let [uid (gensym)]
                  (on-change (vec (conj intervals (assoc (props-interval/new-interval) :tasks {} :_uid uid))))
                  (set-pending-scroll [(interval-item-uid uid)])))}
             "Add interval"]}))]}
     (for [[idx v] (map-indexed vector intervals)
           :let [uid (:_uid v)]]
       [interval
        {:key              uid
         :uid              uid
         :idx              idx
         :value            v
         :display-settings display-settings
         :on-add-interval  (when (= idx max-interval-idx)
                             (fn []
                              (let [uid (gensym)]
                                (on-change (vec (conj intervals (assoc (props-interval/new-interval) :tasks {} :_uid uid))))
                                (set-pending-scroll [(interval-item-uid uid)]))))
         :on-change (comp on-change (get-update idx))}])]))

(defn- c-timesheet [{:keys [value on-change display-settings]}]
  (let [{:keys [person intervals]} value]
    [pf/c-form {:class :ncr-c-property-timesheet :on-submit #(.preventDefault %)}
     [label-input {:label "Person" :value person :on-change #(on-change (assoc value :person %))}]
     [interval-list
      {:value intervals
       :display-settings display-settings
       :on-change #(on-change (assoc value :intervals %))}]]))

(defn- c-settings-menu [{:keys [value on-change]}]
  [pf/c-dropdown-menu {:class :ncr-c-property-timesheet__dropdown :toggle {:icon [w/c-toolbar-icon {:icon :apps}]}}
   [pf/c-menu {:is-plain true}
    [pf/c-menu-content
     (let [{:keys [show-task-groups?]} value]
       [pf/c-menu-item
        {:is-selected show-task-groups?
         :on-click (u/isolate* #(on-change (assoc value :show-task-groups? (not show-task-groups?))))}
        "Show task groups"])
     [pf/c-divider]
     [pf/c-dropdown-group
      {:label "Days and hours"}
      [pf/c-menu-list
       (for [[k title]
             [[:lowercase? "Lowercase"] [:days-first? "Days first"] [:days-no-space? "No space"]]
             :let [v (get value k)]]
         [pf/c-menu-item
          {:key k
           :is-selected v
           :on-click
           (u/isolate* #(on-change (assoc value k (not v))))}
          title])]]]]])

(defn- c-edit-dialog [{:keys [data title subtitle update!]}]
  (let [*data (uix/state data)
        data @*data
        *status (uix/state nil)
        updating? (= :updating @*status)
        *display-settings (pp/use-timesheet-settings)
        display-settings @*display-settings]
    [m/c-modal
     {:variant :medium
      :id :timesheet-props-modal
      :actions
      [[pf/c-button
        {:variant :primary
         :is-disabled updating?
         :on-click
         (fn []
           (reset! *status :updating)
           (go (let [status (<! (update! (sanitize-data data) "Timesheet: [modified]"))]
                 (if (not= :error status) (m/close! nil) (reset! *status status)))))}
        "Ok"]
       [pf/c-button {:variant :link :is-disabled updating? :on-click #(m/close! nil)} "Cancel"]]
      :header [pf/l-flex
               [pf/l-flex {:grow {:default "grow"} :direction "column" :space-items {:default "spaceItemsNone"}}
                [:h1 {:class :pf-c-modal-box__title} [:span {:class :pf-c-modal-box__title-text} title]]
                (when subtitle
                  [:h4 {:class :pf-c-modal-box__title
                        :style {"--pf-c-modal-box__title--FontSize" "14px"
                                :color "var(--pf-global--Color--400)"}}
                   [:span {:class :pf-c-modal-box__title-text} subtitle]])]
               [pf/c-tooltip {:content [:span "Preferences"]}
                [pf/c-toolbar-item [c-settings-menu {:value display-settings :on-change #(reset! *display-settings %)}]]]]}
     [c-timesheet {:value data :display-settings display-settings :on-change (partial reset! *data)}]]))


(defn- update! [fragment-id property-key updated body]
  (let [ch (async/chan)]
    (go
      (let [patched
            (<!
              (a/!operation
                {:operation-title "Updating Property" :success-title "Property Updated" :body body}
                (b/!mutation
                  [:properties_patch
                   {:reference fragment-id
                    :properties (u/transit-write {property-key updated})}])))
            patched (u/transit-read patched)]
        (async/put! ch
          (if (= :error (first patched)) :error :success))))
    ch))

(defn- c-load-and-edit-dialog [{:keys [fragment-id on-updated]}]
  (let
    [*data (uix/state nil)
     state
     (ps/sub-reducer-map! {}
       {::state
        (fn [_ state]
          (reset! *data (get-in state [:data :properties PROPERTY_KEY]))
          state)

        ::load
        (fn [_state]
          (go
            (u/when-fail
              [error
               (u/error-m
                 [{fragment :fragment_ properties :properties}
                  (<!
                    (b/!multi-query
                      {:fragment_ [:fragment_load {:reference fragment-id} [:id :name :writableHint]]
                       :properties [:properties_load {:reference fragment-id}]}))
                  properties (u/transit-read properties)]
                 (ps/pub! [::state {:status :loaded :data {:fragment fragment :properties properties}}]))]
              (do
                (ps/pub! [::state {:status :error :reason error}])
                (log/error :error error))))
          {:status :progress})})
     *update-status (uix/state nil)
     updating? (= :updating @*update-status)

     {loaded :data :keys [status]} @state
     loading? (= :progress status)
     data @*data]

    (uix/with-effect [fragment-id]
      (ps/pub! [::load]))

    [m/c-modal
     {:title [:<> "Edit timesheet"
              [:h3
               (if loading?
                 "Please wait..."
                 [fragment-common/c-name
                  {:open-in-new-tab? true
                   :editable? false
                   :style {:font-size :inherit}
                   :class :pf-u-p-0}
                  (:fragment loaded)])]]
      :variant :medium
      :id :timesheet-props-modal
      :actions
      [(when-not loading?
         [pf/c-button
          {:variant :primary
           :is-disabled updating?
           :on-click
           (fn []
             (reset! *update-status :updating)
             (go (let [status (<! (update! fragment-id PROPERTY_KEY (sanitize-data data) "Timesheet: [modified]"))]
                   (if (not= :error status)
                     (do (m/close! nil)
                       (when on-updated (on-updated)))
                     (reset! *update-status status)))))}
          "Ok"])
       [pf/c-button {:variant :link :is-disabled updating? :on-click #(m/close! nil)} "Cancel"]]}
     (if loading?
       [w/c-empty-progress "Loading..."]
       [c-timesheet {:value data :on-change (partial reset! *data)}])]))

(defn c-timesheet-property [props initial update! {:keys [fragment-name]}]
  (let [label (get props :label "")
        *display-settings (pp/use-timesheet-settings)]
    [pf/c-form-group
     {:class :ncr-c-drawer-panel-properties__timesheet :label label}
     [pf/c-input-group
      [pf/c-button
       {:class (when (seq initial) :pf-u-w-100)
        :variant :secondary
        :on-click (fn []
                    (m/!modal #'c-edit-dialog {:data initial :title "Timesheet" :subtitle fragment-name :update! update!}))}
       (if (seq initial)
         [timesheet-title (assoc initial :wrap? true :display-settings @*display-settings)]
         [w/c-mdi-icon {:icon :plus}])]
      (when initial
        [pf/c-tooltip {:content [:span "Remove timesheet"] :position :auto :enable-flip true}
         [pf/c-button {:variant "plain" :class :ncr-c-drawer-panel-properties__timesheet-reset}
          [w/c-mdi-icon {:icon :delete-outline :on-click #(update! nil "Timesheet: [removed]")}]]])]]))

(defn c-timesheet-label [value]
  (let [*display-settings (pp/use-timesheet-settings)]
    [:<>
     [w/c-mdi-icon {:icon :progress-clock :class :pf-u-pr-xs}]
     [timesheet-title (assoc value :show-time? false :display-settings @*display-settings)]]))

(defn edit-fragment-property [{:keys [id on-updated]}]
  (m/!modal #'c-load-and-edit-dialog {:fragment-id id :on-updated on-updated}))

